import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import './Blog.css';
import { useBlog } from './contexts/BlogContext';

const BlogDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // Use the blog context
    const {
        getPostById,
        getRelatedPosts,
        fetchBlogContent,
        contentCache,
        updatePostVotes,
        getPostVotes,
        setUserVoted,
        incrementDownvoteCount,
        loading: contextLoading,
        error: contextError
    } = useBlog();

    const [post, setPost] = useState(null);
    const [content, setContent] = useState('');
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Add voting state
    const [upvotes, setUpvotes] = useState(0);
    const [downvotes, setDownvotes] = useState(0);
    const [hasVoted, setHasVoted] = useState(false);
    const [downvoteCount, setDownvoteCount] = useState(0);

    useEffect(() => {
        // Scroll to top when component mounts
        // window.scrollTo(0, 0);

        const loadPostData = async () => {
            try {
                setLoading(true);

                // Get post data from context
                const currentPost = getPostById(id);

                if (!currentPost) {
                    throw new Error('Blog post not found');
                }

                setPost(currentPost);

                // Fetch the content markdown file using the context
                const markdownContent = await fetchBlogContent(currentPost.contentFile);
                setContent(markdownContent);

                // Get related posts from context
                const related = getRelatedPosts(id);
                setRelatedPosts(related);

                // Get voting data from context
                const voteData = getPostVotes(id);
                setUpvotes(voteData.upvotes);
                setDownvotes(voteData.downvotes);
                setHasVoted(voteData.userVoted);
                setDownvoteCount(voteData.downvoteCount);

                setLoading(false);
            } catch (err) {
                console.error('Error loading blog post:', err);
                setError(err.message);
                setLoading(false);
            }
        };

        // Only load post data if context is ready
        if (!contextLoading) {
            loadPostData();
        }
    }, [id, getPostById, getRelatedPosts, getPostVotes, contextLoading]);

    // Handle navigation to different blog post
    useEffect(() => {
        if (post) {
            // Reset loading state when navigating to a new post
            setLoading(true);

            // Fetch the content markdown file for the new post
            const fetchContent = async () => {
                try {
                    // Check if content is already in cache
                    if (contentCache[post.contentFile]) {
                        setContent(contentCache[post.contentFile]);
                        setLoading(false);
                        return;
                    }

                    // Otherwise fetch using the context method
                    const markdownContent = await fetchBlogContent(post.contentFile);
                    setContent(markdownContent);
                    setLoading(false);
                } catch (err) {
                    console.error('Error fetching blog content:', err);
                    setError(err.message);
                    setLoading(false);
                }
            };

            fetchContent();
        }
    }, [post?.contentFile, contentCache, fetchBlogContent]);

    // Handle upvote
    const handleUpvote = () => {
        if (!hasVoted) {
            const newUpvotes = updatePostVotes(id, 'upvotes');
            setUpvotes(newUpvotes);
            setHasVoted(true);
            setUserVoted(id, true);
        }
    };

    // Handle downvote
    const handleDownvote = () => {
        const newDownvoteCount = incrementDownvoteCount(id);
        setDownvoteCount(newDownvoteCount);

        // Check if this is the third or more downvote
        if (newDownvoteCount >= 3) {
            // Navigate to the "joke" page
            navigate('/downvote-donations');
            return;
        }

        if (!hasVoted) {
            const newDownvotes = updatePostVotes(id, 'downvotes');
            setDownvotes(newDownvotes);
            setHasVoted(true);
            setUserVoted(id, true);
        }
    };

    if (loading || contextLoading) {
        return (
            <div className="blog-detail-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading blog post...</p>
                </div>
            </div>
        );
    }

    if (error || contextError) {
        return (
            <div className="blog-detail-container">
                <div className="error-message">
                    <h2>Error Loading Blog Post</h2>
                    <p>{error || contextError}</p>
                    <div className="error-buttons">
                        <button onClick={() => navigate('/blog')} className="secondary-button">
                            Back to Blog
                        </button>
                        <button onClick={() => window.location.reload()} className="primary-button">
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!post) return null;

    return (
        <div>
            {/* Navigation */}
            <nav className="main-nav">
                <div className="nav-container">
                    <Link to="/" className="logo">Julian<span>.dev</span></Link>

                    {/* Desktop Navigation */}
                    <div className="desktop-nav">
                        <Link to="/" className="nav-link">Home</Link>
                        <Link to="/#about" className="nav-link">About</Link>
                        <Link to="/#experience" className="nav-link">Experience</Link>
                        <Link to="/#projects" className="nav-link">Projects</Link>
                        <Link to="/#skills" className="nav-link">Skills</Link>
                        <Link to="/blog" className="nav-link active">Blog</Link>
                        <Link to="/#contact" className="nav-link">Contact</Link>
                    </div>

                    {/* Mobile Navigation Button */}
                    <button className="mobile-menu-button">
                        ☰
                    </button>
                </div>

                {/* Mobile Navigation Menu */}
                <div className="mobile-nav" style={{ display: 'none' }}>
                    <Link to="/" className="nav-link">Home</Link>
                    <Link to="/#about" className="nav-link">About</Link>
                    <Link to="/#experience" className="nav-link">Experience</Link>
                    <Link to="/#projects" className="nav-link">Projects</Link>
                    <Link to="/#skills" className="nav-link">Skills</Link>
                    <Link to="/blog" className="nav-link active">Blog</Link>
                    <Link to="/#contact" className="nav-link">Contact</Link>
                </div>
            </nav>

            <div className="blog-detail-container">
                <div className="blog-detail-header">
                    <Link to="/blog" className="back-button">
                        <i className="fa fa-arrow-left"></i> Back to Blog
                    </Link>

                    <div className="blog-detail-meta">
                        <span className="blog-detail-category">{post.category}</span>
                        <span className="blog-detail-date">{post.date}</span>
                        <span className="blog-detail-read-time">{post.readTime}</span>
                    </div>

                    <h1 className="blog-detail-title">{post.title}</h1>

                    <div className="blog-detail-author">
                        <img src={`/images/blog/${post.authorImage}`} alt={post.author} className="author-image" />
                        <span className="author-name">By {post.author}</span>
                    </div>
                </div>

                <div className="blog-detail-featured-image">
                    <img src={`/images/blog/${post.image}`} alt={post.title} />
                </div>

                <div className="blog-detail-content markdown-content">
                    <ReactMarkdown>{content}</ReactMarkdown>
                </div>

                {/* Vote Section */}
                <div className="blog-vote-section">
                    <h3>Was this article helpful?</h3>
                    <div className="vote-buttons">
                        <button
                            className={`vote-button upvote ${hasVoted ? 'voted' : ''}`}
                            onClick={handleUpvote}
                            disabled={hasVoted}
                        >
                            <i className="fa fa-thumbs-up"></i>
                            <span>{upvotes}</span>
                        </button>
                        <button
                            className={`vote-button downvote ${hasVoted ? 'voted' : ''}`}
                            onClick={handleDownvote}
                        >
                            <i className="fa fa-thumbs-down"></i>
                            <span>{downvotes}</span>
                        </button>
                    </div>
                </div>

                <div className="blog-detail-tags">
                    {post.tags.map((tag, index) => (
                        <span key={index} className="blog-tag">{tag}</span>
                    ))}
                </div>

                <div className="blog-share">
                    <span className="share-text">Share this article:</span>
                    <div className="share-buttons">
                        <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="share-button">
                            <i className="fa fa-twitter"></i>
                        </a>
                        <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="share-button">
                            <i className="fa fa-linkedin"></i>
                        </a>
                        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="share-button">
                            <i className="fa fa-facebook"></i>
                        </a>
                        <button onClick={() => {
                            navigator.clipboard.writeText(window.location.href);
                            alert('Link copied to clipboard!');
                        }} className="share-button">
                            <i className="fa fa-link"></i>
                        </button>
                    </div>
                </div>

                <div className="blog-author-box">
                    <img src={`/images/blog/${post.authorImage}`} alt={post.author} className="author-profile" />
                    <div className="author-info">
                        <h3>{post.author}</h3>
                        <p>Julian is a Senior iOS Engineer with over 10 years of experience building mobile applications. He specializes in creating intuitive, performant apps using Swift, SwiftUI, and modern iOS frameworks.</p>
                        <div className="author-social">
                            <a href="https://github.com/julpanucci" target="_blank" rel="noopener noreferrer" className="author-social-link">
                                <i className="fa fa-github"></i>
                            </a>
                            <a href="https://linkedin.com/in/julpanucci" target="_blank" rel="noopener noreferrer" className="author-social-link">
                                <i className="fa fa-linkedin"></i>
                            </a>
                            <a href="https://twitter.com/julpanucci" target="_blank" rel="noopener noreferrer" className="author-social-link">
                                <i className="fa fa-twitter"></i>
                            </a>
                        </div>
                    </div>
                </div>

                {relatedPosts.length > 0 && (
                    <div className="related-posts">
                        <h2 className="related-title">Related Articles</h2>
                        <div className="related-grid">
                            {relatedPosts.map((post) => (
                                <div className="related-card" key={post.id}>
                                    <div className="related-image">
                                        <img src={`/images/blog/${post.image}`} alt={post.title} />
                                    </div>
                                    <div className="related-content">
                                        <span className="related-category">{post.category}</span>
                                        <h3>{post.title}</h3>
                                        <div className="related-info">
                                            <span className="related-date">{post.date}</span>
                                            <span className="related-read-time">{post.readTime}</span>
                                        </div>
                                        <Link to={`/blog/${post.id}`} className="related-link">
                                            Read Article <i className="fa fa-arrow-right"></i>
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <footer className="footer">
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-logo">
                            <Link to="/" className="footer-logo-link">Julian<span>.dev</span></Link>
                            <p>Senior iOS Engineer</p>
                        </div>
                        <div className="footer-nav">
                            <Link to="/" className="footer-link">Home</Link>
                            <Link to="/#about" className="footer-link">About</Link>
                            <Link to="/#experience" className="footer-link">Experience</Link>
                            <Link to="/#projects" className="footer-link">Projects</Link>
                            <Link to="/#skills" className="footer-link">Skills</Link>
                            <Link to="/blog" className="footer-link">Blog</Link>
                            <Link to="/#contact" className="footer-link">Contact</Link>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <p>&copy; {new Date().getFullYear()} Julian Panucci. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default BlogDetail;